<template lang="pug">
  #game-summary
    #summary
      h1 Pong
      span(:style="mutedTextStyles") Arcade
      p A simple game of Pong.
    .game
      canvas#canvas
        | HTML5 canvas appears to be unsupported in the current browser.
        br
        | Please try updating or use a different browser.
      #status
        #status-progress(style='display: none;' oncontextmenu='event.preventDefault();')
          #status-progress-inner
        #status-indeterminate(style='display: none;' oncontextmenu='event.preventDefault();')
          div
          div
          div
          div
          div
          div
          div
          div
        #status-notice.godot(style='display: none;')
    #controls
      h2(:style="mutedTextStyles") Controls &#x1F3AE;
      .control(v-for="control in game.controls")
        h3(:style="mutedTextStyles") {{ control.name }}
        .keybinds
          .keybind(v-for="(keybind, key) in control.action")
            span {{ key }}
            span {{ keybind }}
    #resources
      h2(:style="mutedTextStyles") Resources &#x1F4C2;
      .resources-box
        a.resource(v-for="(value, key) in game.resources" target="_blank" :href="value" :style="colorStyles") {{ key }}
    #goals.criterias
      h2(:style="mutedTextStyles") Goals &#x1F3AF;
      .criteria-box
        .criteria(v-for="goal in game.goals")
          p(v-if="goal.type === 'text'" :style="textColorStyle") {{ goal.content }}
          .list-box(v-else)
            .list-item(v-for="item in goal.items")
              span &#x1F3AF;
              p(:style="textColorStyle") {{ item }}
    #successes.criterias
      h2(:style="mutedTextStyles") Successes &#x1F44C;
      .criteria-box
        .criteria(v-for="success in game.successes")
          p(v-if="success.type === 'text'" :style="textColorStyle") {{ success.content }}
          .list-box(v-else)
            .list-item(v-for="item in success.items")
              span &#x1F44C;
              p(:style="textColorStyle") {{ item }}
    #failures.criterias
      h2(:style="mutedTextStyles") Failures &#x1F44E;
      .criteria-box
        .criteria(v-for="failure in game.failures")
          p(v-if="failure.type === 'text'" :style="textColorStyle") {{ failure.content }}
          .list-box(v-else)
            .list-item(v-for="item in failure.items")
              span &#x1F44E;
              p(:style="textColorStyle") {{ item }}
    #bugs.criterias
      h2(:style="mutedTextStyles") Bugs &#x1F41B;
      .criteria-box
        .criteria(v-for="bug in game.bugs")
          p(v-if="bug.type === 'text'" :style="textColorStyle") {{ bug.content }}
          .list-box(v-else)
            .list-item(v-for="item in bug.items")
              span &#x1F41B;
              p(:style="textColorStyle") {{ item }}
    #ideas.criterias
      h2(:style="mutedTextStyles") Ideas &#x1F4A1;
      .criteria-box
        .criteria(v-for="idea in game.ideas")
          p(v-if="idea.type === 'text'" :style="textColorStyle") {{ idea.content }}
          .list-box(v-else)
            .list-item(v-for="item in idea.items")
              span &#x1F4A1;
              p(:style="textColorStyle") {{ item }}
    #conclusions.criterias
      h2(:style="mutedTextStyles") Conclusions &#x1F531;
      .criteria-box
        .criteria(v-for="conclusion in game.conclusions")
          p(v-if="conclusion.type === 'text'" :style="textColorStyle") {{ conclusion.content }}
          .list-box(v-else)
            .list-item(v-for="item in conclusion.items")
              span &#x1F531;
              p(:style="textColorStyle") {{ item }}
    footer
      a.link(href="/" :style="colorStyles") Go Back
</template>

<script>
export default {
  name: "GameSummary",
  props: {
    game: {
      type: Object,
      required: true
    }
  },
  computed: {
    mutedTextStyles() {
      return {
        '--color-muted': this.game.colors.muted
      }
    },
    accentColorStyle() {
      return {
        '--color-accent': this.game.colors.accent
      }
    },
    textColorStyle() {
      return {
        '--color-text': this.game.colors.text
      }
    },
    colorStyles() {
      return {
        '--color-muted': this.game.colors.muted,
        '--color-accent': this.game.colors.accent,
        '--color-text': this.game.colors.text,
        '--color-base': this.game.colors.base
      }
    }
  },
  mounted() {
    this.game.styles.forEach(style => {
      const gameStyle = document.createElement("link")
      gameStyle.setAttribute("rel", "stylesheet")
      gameStyle.setAttribute("type", "text/css")
      gameStyle.setAttribute("href", `/assets/games/${this.game.slug}/${style}`)
      document.head.appendChild(gameStyle)
    })
    this.game.scripts.forEach(script => {
      const gameScript = document.createElement("script")
      gameScript.setAttribute("src", `/assets/games/${this.game.slug}/${script}`)
      gameScript.async = true
      document.head.appendChild(gameScript)
    })
  }
}
</script>

<style lang="stylus">
#game-summary
  display grid
  grid-template-columns auto
  margin-top s0
  @media (min-width: 480px)
    margin-left auto
    margin-right auto
    max-width 420px
  @media (min-width: 768px)
    max-width 680px
  @media (min-width: 1080px)
    grid-template-columns 1fr fit-content(60%)
    margin 0
    padding sp6 0 0
    max-width none
  @media (min-width: 1280px)
    margin 0 auto
    max-width 1280px
  > #summary
    box-sizing border-box
    color white
    padding (sp7+s0) sp2 0
    width 100%
    height 100vh
    max-height 45vh
    > span
      color var(--color-muted)
      display block
      margin-bottom s0
    > p
      font-size sp1
    @media (min-width: 1080px)
      padding sp8 sp2 0
      height auto
      max-height none
    @media (min-width: 1280px)
      > h1
        font-size sp4
      > span
        font-size sp1
      > p
        font-size sp2
  > .game
    position relative
    padding-bottom 56.25%
    margin 0 auto sp2
    height 0
    width "calc(100% - %s)" % sp2
    @media (min-width: 1080px)
      box-sizing border-box
      margin 0 0 0 auto
      padding-top sp8
      padding-left sp2
      padding-right sp2
    > canvas
      bs2(rgba(black, 0.4))
      width 100%
  > #controls
    box-sizing border-box
    display grid
    grid-template-columns 1fr
    column-gap s0
    row-gap sp1
    padding (sp7+s0) sp2 0
    width 100%
    > h2
      color var(--color-muted)
      font-size tp1
      text-align center
    > .control
      > h3
        color var(--color-muted)
        font-size tm2
        font-weight 700
        letter-spacing m5
        margin-bottom sm4
        text-transform uppercase
      > .keybinds
        color white
        > .keybind
          display grid
          grid-template-columns minmax(0, 1fr) 1fr
          column-gap sm4
          margin-bottom sm4
    @media (min-width: 768px)
      grid-template-columns 1fr 1fr
      > h2
        grid-column 1/-1
    @media (min-width: 1080px)
      grid-column span 2
      margin 0 auto
      max-width 60%
    @media (min-width: 1280px)
      grid-template-columns 1fr 1fr 1fr
      padding-top sp8
      max-width 80%
      > h2
        font-size tp3
        margin-bottom sp2
      > .control
        > h3
          font-size t0
          margin-bottom sm2
        > .keybinds > .keybind 
          margin-bottom sm2
          > span
            font-size tp1
  > #resources
    box-sizing border-box
    padding (sp7+s0) sp2 0
    width 100%
    > h2
      color var(--color-muted)
      font-size tp1
      margin-bottom s0
      text-align center
    > .resources-box
      display flex
      flex-direction column
      @media (min-width: 768px)
        display grid
        grid-template-columns 1fr 1fr 1fr
        column-gap s0
        > a.resource
          display block
      > a.resource
        border 1px dashed var(--color-accent)
        color var(--color-accent)
        margin-bottom s0
        padding s0
        text-align center
        text-decoration none
        transition all 0.2s ease-in-out
        &:hover
          border 1px solid var(--color-accent)
          background var(--color-accent)
          color var(--color-base)
    @media (min-width: 1080px)
      grid-column span 2
      margin 0 auto
      max-width 60%
    @media (min-width: 1280px)
      padding-top sp8
      max-width 80%
      > h2
        font-size tp3
        margin-bottom sp2
  > .criterias
    box-sizing border-box
    padding (sp7+s0) sp2 0
    width 100%
    > h2
      color var(--color-muted)
      font-size tp1
      margin-bottom s0
      text-align center
    > .criteria-box
      display grid
      grid-template-columns 1fr
      row-gap s0
      @media (min-width: 768px)
        margin-left auto
        margin-right auto
        max-width 80%
      @media (min-width: 1080px)
        max-width 60%
      @media (min-width: 1280px)
        grid-template-columns 1fr 1fr
        column-gap sp2
        max-width none
        > .criteria
          grid-column span 1
          &:only-child
            grid-column span 2
            margin 0 auto
            width 50%
      > .criteria
        > p
          color var(--color-text)
          line-height 150%
        > .list-box
          > .list-item
            display flex
            margin-bottom sm2
            > span
              margin-right s0
            > p
              color var(--color-text)
              line-height 150%
    @media (min-width: 1080px)
      grid-column span 2
    @media (min-width: 1280px)
      padding-top sp8
      > h2
        font-size tp3
        margin-bottom sp2
      > .criteria-box
        > .criteria
          > p
            font-size tp1
          > .list-box > .list-item
            > span, > p
              font-size tp1
  > footer
    box-sizing border-box
    display flex
    padding (sp7+s0) sp2 (sp7+s0)
    width 100%
    @media (min-width: 1080px)
      grid-column span 2
    > a.link
      color var(--color-muted)
      font-size sp1
      font-weight 600
      text-align center
      text-decoration none
      width 100%
      transition all 0.2s ease-in-out
      &:hover
        color var(--color-text)
</style>